import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";
import { Checkbox, FieldError, FileUpload, SegmentedControl, SignaturePad } from "../../ui";
import { loadImageFile, prepareSignature } from "../../Settings/invoice/signatureImage";

const MODES = [
  { value: "draw", label: "Draw it" },
  { value: "upload", label: "Upload a photo or scan" },
];

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error("The signature could not be read."));
  reader.readAsDataURL(blob);
});

/**
 * SignatureCapture — a signature as a PNG data URL, drawn on the pad or taken
 * from a photo. The upload is the keyboard-reachable path the pad cannot be.
 *
 * `onChange(dataUrl | null)` whenever what would be submitted changes. The
 * image is trimmed to the ink (and, for a photo, made transparent where it is
 * paper-white) by the same rules the invoice signatory's signature uses.
 */
const SignatureCapture = ({ onChange, disabled = false }) => {
  const [mode, setMode] = useState("draw");
  const [files, setFiles] = useState([]);
  const [removeBackground, setRemoveBackground] = useState(true);
  const [error, setError] = useState("");
  const padRef = useRef(null);

  const fromCanvas = async (hasInk) => {
    if (!hasInk) { onChange(null); return; }
    try {
      const { blob } = await prepareSignature(padRef.current.canvas, { removeBackground: false });
      onChange(await blobToDataUrl(blob));
      setError("");
    } catch (e) {
      setError(e.message);
      onChange(null);
    }
  };

  const fromFile = async (picked, clear = removeBackground) => {
    setFiles(picked);
    if (!picked[0]) { onChange(null); return; }
    try {
      const img = await loadImageFile(picked[0]);
      const { blob } = await prepareSignature(img, { removeBackground: clear });
      onChange(await blobToDataUrl(blob));
      setError("");
    } catch (e) {
      setError(e.message);
      onChange(null);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setError("");
    onChange(null);
    if (next === "draw") setFiles([]);
    else padRef.current?.clear();
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      <SegmentedControl label="How to sign" options={MODES} value={mode} onChange={switchMode} />
      {mode === "draw" ? (
        <SignaturePad ref={padRef} onChange={fromCanvas} />
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-2">
          <FileUpload
            files={files}
            onChange={(picked) => fromFile(picked)}
            accept=".jpg,.jpeg,.png,.webp"
            multiple={false}
            maxFiles={1}
            maxSizeMB={5}
            hint="Choose a photo or scan of your signature"
            disabled={disabled}
          />
          <Checkbox
            size="sm"
            label="Make the paper transparent"
            description="Pixels that are paper-white vanish, so the signature sits on the document like ink."
            checked={removeBackground}
            onChange={(e) => { setRemoveBackground(e.target.checked); if (files[0]) fromFile(files, e.target.checked); }}
          />
        </div>
      )}
      {error && <FieldError>{error}</FieldError>}
      <p className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-content-subtle">
        <Upload size={12} aria-hidden="true" /> The signature is placed on the agreement exactly as drawn.
      </p>
    </div>
  );
};

export default SignatureCapture;
